'use client';

import { Button } from '@pickleballinc/ui/components/ui/button';
import { useState } from 'react';

import { SubPlayerModal } from '@/components/organisms/ScoreCards/subs/SubPlayerModal';
import SubsListSelectModal from '@/components/organisms/ScoreCards/subs/SubsListSelectModal';
import { Matchup, TeamLeaguePlayerInfo } from '@/entities';
import { MatchupStatusEnum } from '@/entities/matchup/constants';
import { RotationsInfo } from '@/entities/rotations/RotationsInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useCountriesQuery } from '@/hooks/lookups/useCountriesQuery';
import { useStatesQuery } from '@/hooks/lookups/useStatesQuery';

interface ScoreCardsLayoutProps {
	children: React.ReactNode;
	displaySubBtn?: boolean;
	title: string;
	matchupData?: Matchup;
	rotationsInfo?: RotationsInfo;
	teamOnePlayers?: TeamLeaguePlayerInfo[];
	teamTwoPlayers?: TeamLeaguePlayerInfo[];
	substitutePlayers?: TeamLeaguePlayerInfo[];
	hideEditLineup?: boolean;
}

export const ScoreCardsLayout = ({
	children,
	displaySubBtn = false,
	title,
	matchupData,
	rotationsInfo,
	teamOnePlayers = [],
	teamTwoPlayers = [],
	substitutePlayers = [],
	hideEditLineup
}: ScoreCardsLayoutProps) => {
	const teamLeagueInfo = useTeamLeagueInfo();

	const [openSubsListSelect, setOpenSubsListSelect] = useState(false);
	const [openSubPlayerModal, setOpenSubPlayerModal] = useState(false);
	const [selectedSubsList, setSelectedSubsList] = useState<'team' | 'event' | 'users'>('team');

	const { data: countries } = useCountriesQuery();
	const { data: states } = useStatesQuery();

	const isSubstitutionAllowed = (): boolean => {
		const isInProgressOrReadyToStart =
			matchupData?.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
			matchupData?.matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS;

		if (!isInProgressOrReadyToStart) {
			return false;
		}
		return true;
	};

	return (
		<div className="flex w-full flex-col">
			<div className="py-9">
				<div className="w-full flex justify-between items-center">
					<h2 className="text-base font-semibold text-gray-900 md:text-2xl">{title}</h2>
					{displaySubBtn && isSubstitutionAllowed() && !hideEditLineup ? (
						<Button
							className="text-sm font-semibold leading-5"
							variant="outline"
							onClick={() => {
								setOpenSubsListSelect(true);
							}}
						>
							Substitute
						</Button>
					) : null}
				</div>
				{children}
			</div>

			{openSubsListSelect ? (
				<SubsListSelectModal
					isOpen={openSubsListSelect}
					setIsOpen={setOpenSubsListSelect}
					onSelect={(list: 'team' | 'event' | 'users') => {
						setOpenSubsListSelect(false);
						setOpenSubPlayerModal(true);
						setSelectedSubsList(list);
					}}
					useScheduleGroups={teamLeagueInfo.useScheduleGroups}
				/>
			) : null}

			<SubPlayerModal
				rotationsInfo={rotationsInfo}
				open={openSubPlayerModal}
				onOpenChange={setOpenSubPlayerModal}
				type={selectedSubsList}
				teamOnePlayers={teamOnePlayers}
				teamTwoPlayers={teamTwoPlayers}
				teamOneTitle={matchupData?.teamOneTitle ?? ''}
				teamTwoTitle={matchupData?.teamTwoTitle ?? ''}
				substitutePlayers={substitutePlayers}
				states={states?.data || []}
				countries={countries?.data || []}
				matches={matchupData?.matches ?? []}
				matchupUuid={matchupData?.uuid ?? ''}
				matchupData={matchupData}
			/>
		</div>
	);
};
